import React from "react";
import {DeltaStatic} from "quill";
import ReactQuill from 'react-quill';
import * as showdown from "showdown";
import 'react-quill/dist/quill.snow.css';
import "./RichTextEditor.scss";
import {Alert, ExclamationCircleIcon, Flex, FormLabel, Text} from "@fluentui/react-northstar";
import languageObject from "../../translations";

interface IRichTextEditorProps {
    id: string,
    value: string,
    label?: string,
    inputHeight: number,
    onChange: (content: string) => void,
    max?: number,
    disableToolbar?: boolean,
}

interface IRichTextEditorState {
    textLength: number,
    showWarningPasteMessage: boolean,
}

/**
 * Richtext editor
 */
class RichTextEditor extends React.Component<IRichTextEditorProps, IRichTextEditorState> {

    private editorRef = React.createRef<ReactQuill>();
    private readonly parser = new showdown.Converter();
    private editorMarkdownTextRef = React.createRef<string | null>();

    constructor(props: IRichTextEditorProps) {
        super(props);

        this.state = {
            textLength: 0,
            showWarningPasteMessage: false,
        }
    }

    shouldComponentUpdate(nextProps: Readonly<IRichTextEditorProps>, nextState: Readonly<IRichTextEditorState>): boolean {
        return this.props.id !== nextProps.id
            || this.state.textLength !== nextState.textLength
            || this.state.showWarningPasteMessage !== nextState.showWarningPasteMessage
            || this.props.value !== nextProps.value;
    }

    componentDidUpdate(prevProps: Readonly<IRichTextEditorProps>, prevState: Readonly<IRichTextEditorState>, snapshot?: any) {
        this.refreshEditorText(prevProps.value, this.props.value);
    }

    refreshEditorText = (prevText: string, newText: string) => {
        if (!this.editorRef.current) return;
        const editorMarkdownText = this.editorMarkdownTextRef.current ?? "";
        const hasTextChanged = prevText !== newText;
        const needToUpdateEditorText = newText !== editorMarkdownText;
        if (!hasTextChanged || !needToUpdateEditorText) return;
        this.editorRef.current?.getEditor().setText(this.props.value, "silent");
    }

    handleChange = (content: string, textLength: number) => {
        let parsedContent = this.parser.makeMarkdown(content);
        parsedContent = parsedContent.replace(new RegExp("<!-- -->", 'g'), "").trim();
        parsedContent = parsedContent.replace(/\*\*\s*(.*?)\s*\*\*/g, "**$1**");
        parsedContent = parsedContent.replace(/\*\s*(.*?)\s*\*/g, "*$1*");
        if (this.props.value === parsedContent) return;
        // @ts-ignore
        this.editorMarkdownTextRef.current = parsedContent;
        this.props.onChange(parsedContent);
        this.handleLength(textLength);
    };

    handleLength = (length: number) => {
        this.setState({textLength: length});
    }

    handleShowPasteWarningMessage = () => {
        this.setState({showWarningPasteMessage: true});
        setTimeout(() => this.setState({showWarningPasteMessage: false}), 2000);
    }

    handleQuillRef = (ref: React.RefObject<ReactQuill>) => {
        this.editorRef = ref;
    }

    render() {
        return (
            <Flex column fill className={"form-input"}>
                <FormLabel content={this.props.label}/>
                <Flex column fill
                      className={"richTextContainer " + (this.props.disableToolbar ? "toolbar-disabled" : "")}
                      style={{height: this.props.inputHeight + "px"}}>
                    <Alert
                        styles={{position: "absolute", top: "3px", right: 0, zIndex: 1000}}
                        visible={this.state.showWarningPasteMessage}
                        content={
                            <Flex gap={"gap.smaller"} vAlign={"center"}>
                                <ExclamationCircleIcon outline/>
                                <Text content={languageObject.translate("CharacterLimitReached")}/>
                            </Flex>
                        }
                        warning
                    />
                    {!!this.props.max &&
                        <Text styles={{position: "absolute", right: "0", top: "6px", color: "grey"}}
                              content={this.state.textLength + "/" + this.props.max}/>
                    }
                    <Editor
                        id={this.props.id}
                        value={this.parser.makeHtml(this.props.value ?? "")}
                        handleChange={this.handleChange}
                        handleLength={this.handleLength}
                        handleShowPasteWarningMessage={this.handleShowPasteWarningMessage}
                        max={this.props.max}
                        onQuillRef={this.handleQuillRef}
                        disableToolbar={this.props.disableToolbar}
                    />
                </Flex>
            </Flex>
        )
    }
}

/**
 * Editor props
 */
interface EditorProps {
    onQuillRef?: (ref: React.RefObject<ReactQuill>) => void,
    id: string,
    value: string,
    handleChange: (content: string, textLength: number) => void,
    handleLength: (length: number) => void,
    handleShowPasteWarningMessage: () => void,
    max?: number,
    disableToolbar?: boolean,
}

/**
 * React quill editor
 */
class Editor extends React.Component<EditorProps> {

    private editorRef = React.createRef<ReactQuill>();

    private oldContent: DeltaStatic | undefined = undefined;

    private readonly modules = {
        toolbar: [['bold', 'italic'], [{'list': 'ordered'}, {'list': 'bullet'}]],
    };

    private readonly formats = [
        'bold',
        'italic',
        'link',
        'list',
    ];

    /**
     * Component initialization
     */
    componentDidMount() {
        // Update placeholder of link input quill tooltip
        const tooltipInput = document.querySelector(".ql-tooltip input[data-link]") as HTMLInputElement;
        tooltipInput.dataset.link = "https://www.teams-pro.com/";
        tooltipInput.placeholder = "https://www.teams-pro.com/";

        // Update link label of quill tooltip
        const tooltip = document.querySelector(".ql-tooltip") as HTMLDivElement;
        const label = document.createElement("p");
        label.innerText = languageObject.translate("Link") + ":";
        tooltip.prepend(label);

        // Update actions content
        const styles = `
            .ql-tooltip[data-mode=link] a.ql-action::after {
                content: "${languageObject.translate("Confirm")}" !important;
            }
            
            .ql-tooltip a.ql-action::after {
                content: "${languageObject.translate("Edit")}" !important;
            }
            
            .ql-tooltip a.ql-remove::before {
                content: "${languageObject.translate("Remove")}" !important;
            }
        `
        const styleSheet = document.createElement("style");
        // styleSheet.type = "text/css";
        styleSheet.innerText = styles;
        document.head.appendChild(styleSheet);

        if (!!this.editorRef.current) {
            const textLength = this.editorRef.current?.getEditor().getLength() - 1;
            this.props.handleLength(textLength);
            this.props.onQuillRef?.(this.editorRef);
        }
    }

    /**
     * Allow component to render
     * @param nextProps next props
     * @param nextState next state
     */
    shouldComponentUpdate(nextProps: Readonly<EditorProps>, nextState: Readonly<{}>): boolean {
        return this.props.id !== nextProps.id;
    }

    /**
     * On change content
     * @param content new content
     */
    onChange = (content: string) => {
        if (!!this.editorRef.current) {
            if (content === "<p><br></p>") content = "";
            content = content.replace(/<br>/g, "\n");
            // content = content.replace(/<p>\n<\/p>/g, "\n");
            const textLength = this.editorRef.current?.getEditor().getLength() - 1;
            if (!!this.props.max && textLength <= this.props.max) {
                this.props.handleChange(content, textLength);
                this.oldContent = this.editorRef.current?.getEditor().getContents();
            } else {
                if (!this.oldContent) this.editorRef.current?.getEditor().setText("");
                else this.editorRef.current?.getEditor().setContents(this.oldContent);
                this.props.handleShowPasteWarningMessage();
            }
        }
    }

    render() {
        return (
            <div style={{display: "flex", height: "100%", width: "100%"}}>
                <ReactQuill
                    ref={this.editorRef}
                    theme={"snow"}
                    bounds={".richTextContainer"}
                    scrollingContainer={".quill"}
                    value={this.props.value}
                    onChange={this.onChange}
                    formats={this.formats}
                    modules={this.modules}>
                    <div key={"quill_editor_input"} className={"richTextEditor"}/>
                </ReactQuill>
            </div>
        )
    }
}

export default RichTextEditor;